// Turning pen samples into filled ink outlines (SVG path data).
import { getStroke, type StrokeOptions } from "perfect-freehand";
import type { InkElement, PenKind } from "../../shared/model";

export interface PenPreset {
  label: string;
  /** default nib size in world units */
  size: number;
  opacity: number;
  blend?: "multiply";
  options: StrokeOptions;
}

const easeOutSine = (t: number) => Math.sin((t * Math.PI) / 2);

export const PENS: Record<PenKind, PenPreset> = {
  fountain: {
    label: "Fountain pen",
    size: 3.2,
    opacity: 1,
    options: { thinning: 0.62, smoothing: 0.56, streamline: 0.42, easing: easeOutSine, start: { taper: 0, cap: true }, end: { taper: 0, cap: true } }
  },
  ballpoint: {
    label: "Ballpoint",
    size: 2.2,
    opacity: 1,
    options: { thinning: 0.16, smoothing: 0.6, streamline: 0.4, start: { cap: true }, end: { cap: true } }
  },
  pencil: {
    label: "Pencil",
    size: 2.4,
    opacity: 0.8,
    options: { thinning: 0.42, smoothing: 0.5, streamline: 0.32, start: { taper: 6, cap: true }, end: { taper: 8, cap: true } }
  },
  marker: {
    label: "Marker",
    size: 7,
    opacity: 0.94,
    options: { thinning: 0.02, smoothing: 0.62, streamline: 0.5, start: { cap: true }, end: { cap: true } }
  },
  highlighter: {
    label: "Highlighter",
    size: 18,
    opacity: 0.38,
    blend: "multiply",
    options: { thinning: 0, smoothing: 0.66, streamline: 0.55, start: { cap: true }, end: { cap: true } }
  },
  brush: {
    label: "Calligraphy",
    size: 9,
    opacity: 1,
    options: { thinning: 0.3, smoothing: 0.55, streamline: 0.45 }
  }
};

const f = (n: number) => (Math.round(n * 100) / 100).toString();

/** Quadratic-smoothed closed path through an outline polygon. */
export function outlineToPath(pts: number[][]): string {
  const n = pts.length;
  if (!n) return "";
  if (n < 4) {
    // tiny dab: a circle around the first point
    const [x, y] = pts[0];
    let r = 0;
    for (const p of pts) r = Math.max(r, Math.hypot(p[0] - x, p[1] - y));
    r = Math.max(r / 2, 0.8);
    return `M${f(x - r)},${f(y)}a${f(r)},${f(r)} 0 1,0 ${f(r * 2)},0a${f(r)},${f(r)} 0 1,0 ${f(-r * 2)},0Z`;
  }
  let d = `M${f(pts[0][0])},${f(pts[0][1])}Q${f(pts[1][0])},${f(pts[1][1])} ${f((pts[1][0] + pts[2][0]) / 2)},${f((pts[1][1] + pts[2][1]) / 2)}T`;
  for (let i = 2; i < n - 1; i++) {
    d += `${f((pts[i][0] + pts[i + 1][0]) / 2)},${f((pts[i][1] + pts[i + 1][1]) / 2)} `;
  }
  return d + "Z";
}

export interface InkInput {
  pts: number[]; // flat x, y, pressure
  size: number;
  pen: PenKind;
  sim?: boolean;
  /** pressure curve exponent: < 1 makes light strokes darker */
  gamma?: number;
  /** stroke is still being drawn */
  live?: boolean;
}

function toTriples(flat: number[], gamma: number): number[][] {
  const out: number[][] = [];
  for (let i = 0; i + 2 < flat.length; i += 3) {
    const p = flat[i + 2];
    out.push([flat[i], flat[i + 1], gamma === 1 ? p : Math.pow(Math.max(0.001, p), gamma)]);
  }
  return out;
}

/** Broad-nib calligraphy: a flat nib held at a fixed angle sweeps quads between samples. */
function calligraphyPath(triples: number[][], size: number, sim: boolean): string {
  if (!triples.length) return "";
  // light smoothing so the nib edges don't jitter
  const s: number[][] = [triples[0]];
  for (let i = 1; i < triples.length; i++) {
    const p = s[i - 1];
    const q = triples[i];
    s.push([p[0] + (q[0] - p[0]) * 0.55, p[1] + (q[1] - p[1]) * 0.55, q[2]]);
  }
  const angle = (-40 * Math.PI) / 180;
  const nx = Math.cos(angle);
  const ny = Math.sin(angle);
  let d = "";
  const half = (p: number) => (size / 2) * (sim ? 1 : 0.55 + 0.75 * p);
  if (s.length === 1) {
    const [x, y, p] = s[0];
    const h = half(p);
    return `M${f(x - nx * h)},${f(y - ny * h)}L${f(x + nx * h)},${f(y + ny * h)}L${f(x + nx * h + 0.8)},${f(y + ny * h + 0.8)}L${f(x - nx * h + 0.8)},${f(y - ny * h + 0.8)}Z`;
  }
  for (let i = 1; i < s.length; i++) {
    const [ax, ay, ap] = s[i - 1];
    const [bx, by, bp] = s[i];
    const ha = half(ap);
    const hb = half(bp);
    const quad = [
      [ax + nx * ha, ay + ny * ha],
      [bx + nx * hb, by + ny * hb],
      [bx - nx * hb, by - ny * hb],
      [ax - nx * ha, ay - ny * ha]
    ];
    // make every quad wind the same way so the non-zero fill unions them without holes
    let area = 0;
    for (let k = 0; k < 4; k++) {
      const [x1, y1] = quad[k];
      const [x2, y2] = quad[(k + 1) % 4];
      area += x1 * y2 - x2 * y1;
    }
    if (area < 0) quad.reverse();
    d += `M${f(quad[0][0])},${f(quad[0][1])}L${f(quad[1][0])},${f(quad[1][1])}L${f(quad[2][0])},${f(quad[2][1])}L${f(quad[3][0])},${f(quad[3][1])}Z`;
  }
  return d;
}

export function inkPath(input: InkInput): string {
  const preset = PENS[input.pen] || PENS.fountain;
  const triples = toTriples(input.pts, input.gamma ?? 1);
  if (input.pen === "brush") return calligraphyPath(triples, input.size, !!input.sim);
  const outline = getStroke(triples, {
    ...preset.options,
    size: input.size,
    simulatePressure: !!input.sim && preset.options.thinning !== 0,
    last: !input.live
  });
  return outlineToPath(outline);
}

// Keyed by the points array: moving a stroke copies the element but keeps its points, so no recompute.
const cache = new WeakMap<number[], { size: number; pen: string; sim: boolean; d: string }>();

/** Outline of a stored stroke in the element's local coordinates (cached per points array). */
export function inkElementPath(el: InkElement): string {
  const hit = cache.get(el.pts);
  if (hit && hit.size === el.size && hit.pen === el.pen && hit.sim === !!el.sim) return hit.d;
  const d = inkPath({ pts: el.pts, size: el.size, pen: el.pen, sim: el.sim });
  cache.set(el.pts, { size: el.size, pen: el.pen, sim: !!el.sim, d });
  return d;
}

export function penOpacity(pen: PenKind): number {
  return (PENS[pen] || PENS.fountain).opacity;
}
